'use client';

import { useEffect, useMemo, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient, type UserBet } from '@/lib/api';
import { formatUsdt, liveEstimateNetWinningsMicro, pluralizeRu } from '@/lib/format';
import { getInitData } from '@/lib/tma';

export function AtStakeCard({ refreshKey }: { refreshKey: number }) {
  const [bets, setBets] = useState<UserBet[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const api = createApiClient(getInitData);
    api
      .myBets(100)
      .then((r) => setBets(r.bets.filter((b) => b.status === 'active')))
      .catch((err) => {
        const e = err as ApiClientError;
        setError(e?.message ?? 'Ошибка');
      })
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const summary = useMemo(() => {
    if (!bets || bets.length === 0) return null;
    let totalStake = 0;
    let totalIfAllWin = 0;
    let yesCount = 0;
    let noCount = 0;
    for (const b of bets) {
      totalStake += b.amount_micro;
      const winnings = liveEstimateNetWinningsMicro(
        b.amount_micro,
        b.side,
        b.pool_yes_at_bet_micro,
        b.pool_no_at_bet_micro,
        b.current_pool_yes_micro,
        b.current_pool_no_micro,
        b.market_fee_bps,
      );
      totalIfAllWin += b.amount_micro + winnings;
      if (b.side === 1) yesCount += 1;
      else noCount += 1;
    }
    return {
      count: bets.length,
      totalStake,
      totalIfAllWin,
      potentialGain: totalIfAllWin - totalStake,
      potentialGainPct: totalStake > 0 ? ((totalIfAllWin - totalStake) / totalStake) * 100 : 0,
      yesCount,
      noCount,
    };
  }, [bets]);

  if (loading) {
    return (
      <div className="bg-bg-elev p-5 mb-4 flex justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (error) return null;
  if (!summary || summary.count === 0) return null;

  const yesRatio = summary.count > 0 ? (summary.yesCount / summary.count) * 100 : 50;

  return (
    <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-accent">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em]">
          ● В ИГРЕ СЕЙЧАС
        </div>
        <span className="font-mono text-[10px] text-accent tracking-[0.05em] tabular-nums">
          {summary.count} {pluralizeRu(summary.count, 'СТАВКА', 'СТАВКИ', 'СТАВОК')}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-1">
            СУММА СТАВОК
          </div>
          <div className="display text-[26px] tabular-nums leading-none">
            {formatUsdt(summary.totalStake)}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-1">
            ЕСЛИ ВСЕ ВЫИГРАЮТ
          </div>
          <div className="display text-[26px] tabular-nums leading-none text-yes">
            {formatUsdt(summary.totalIfAllWin)}
          </div>
        </div>
      </div>

      <div className="text-[12px] text-text-secondary mb-3 leading-relaxed">
        Заработаешь до{' '}
        <span className="text-yes font-bold">
          +{formatUsdt(summary.potentialGain)} (+{summary.potentialGainPct.toFixed(0)}%)
        </span>{' '}
        если все ставки выиграют. В худшем случае потеряешь{' '}
        <span className="text-no font-bold">−{formatUsdt(summary.totalStake)}</span>.
      </div>

      {summary.count > 1 && (
        <>
          <div className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-1">
            РАСПРЕДЕЛЕНИЕ ПО СТОРОНАМ
          </div>
          <div className="w-full h-2 bg-bg-elev2 overflow-hidden flex">
            <div className="bg-yes" style={{ width: `${yesRatio}%` }} />
            <div className="bg-no" style={{ width: `${100 - yesRatio}%` }} />
          </div>
          <div className="flex justify-between font-mono text-[10px] text-text-tertiary mt-1 tracking-[0.05em]">
            <span>ДА {summary.yesCount}</span>
            <span>{summary.noCount} NO</span>
          </div>
        </>
      )}
    </div>
  );
}

